import { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Copy, ArrowsClockwise, EnvelopeSimple, Phone, ChatCircleText,
  EnvelopeSimpleOpen, FloppyDisk, PaperPlaneTilt, CheckCircle, House,
} from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { TierBadge, STATUS_OPTIONS } from "./helpers";
import { joinList } from "@/lib/constants";

function Detail({ label, value }) {
  if (value == null || value === "" || (Array.isArray(value) && value.length === 0)) return null;
  return (
    <div>
      <p className="text-[11px] uppercase tracking-[0.15em] text-white/40 mb-1">{label}</p>
      <p className="text-white/90 text-sm">{Array.isArray(value) ? joinList(value) : value}</p>
    </div>
  );
}

function SentBadge({ status }) {
  if (!status) return null;
  const map = {
    sent: { t: "Sent", c: "text-green-400" },
    not_configured: { t: "Ready to send", c: "text-brand" },
    failed: { t: "Send failed", c: "text-red-400" },
  };
  const s = map[status];
  if (!s) return null;
  return <span className={`flex items-center gap-1 text-xs ${s.c}`}><CheckCircle size={13} weight="fill" /> {s.t}</span>;
}

function EditableDraft({ leadId, kind, title, icon: Icon, initial, sentStatus, onSent }) {
  const [text, setText] = useState(initial || "");
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setText(initial || ""); }, [initial]);

  const field = kind === "email" ? "ai_followup_email" : "ai_followup_sms";

  const copy = () => { navigator.clipboard.writeText(text || ""); toast.success(`${title} copied`); };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/leads/${leadId}`, { [field]: text });
      toast.success("Draft saved");
    } finally { setSaving(false); }
  };

  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/leads/${leadId}/send-${kind}`, { body: text });
      if (data.sent) toast.success(`${title} sent!`);
      else toast.message(data.message || "Saved & ready to send.");
      onSent?.(data.status);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSending(false); }
  };

  return (
    <div className="bg-[#0B0C10] border border-white/5 rounded-sm">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
        <span className="flex items-center gap-2 text-sm text-white/80">
          <Icon size={16} weight="duotone" className="text-brand" /> {title}
        </span>
        <div className="flex items-center gap-3">
          <SentBadge status={sentStatus} />
          <button onClick={copy} data-testid={`copy-${kind}`} className="flex items-center gap-1 text-xs text-white/50 hover:text-white"><Copy size={13} /> Copy</button>
        </div>
      </div>
      <textarea
        data-testid={`draft-${kind}`}
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={kind === "email" ? 9 : 3}
        className="w-full bg-transparent px-4 py-4 text-sm text-white/80 leading-relaxed outline-none resize-none"
      />
      <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-white/5">
        <button onClick={save} disabled={saving} data-testid={`save-${kind}`} className="flex items-center gap-1.5 px-4 py-2 rounded-sm border border-white/15 text-xs text-white/70 hover:text-white disabled:opacity-60">
          <FloppyDisk size={13} /> {saving ? "Saving…" : "Save"}
        </button>
        <button onClick={send} disabled={sending} data-testid={`send-${kind}`} className="flex items-center gap-1.5 px-4 py-2 rounded-sm bg-brand text-black text-xs uppercase tracking-wider font-medium hover:bg-brand-hover disabled:opacity-60">
          <PaperPlaneTilt size={13} weight="fill" /> {sending ? "Sending…" : kind === "email" ? "Send email" : "Send SMS"}
        </button>
      </div>
    </div>
  );
}

function AutomationTimeline({ leadId, emailStatus }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    let on = true;
    api.get(`/leads/${leadId}/scheduled-emails`).then(({ data }) => { if (on) setItems(data); }).catch(() => {});
    return () => { on = false; };
  }, [leadId]);

  const fmt = (iso) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const statusChip = {
    sent: { t: "Sent", c: "text-green-400" },
    pending: { t: "Scheduled", c: "text-brand" },
    cancelled: { t: "Cancelled", c: "text-white/30" },
    failed: { t: "Failed", c: "text-red-400" },
  };

  const firstChip = emailStatus === "sent"
    ? { t: "Sent", c: "text-green-400" }
    : emailStatus === "failed"
    ? { t: "Failed", c: "text-red-400" }
    : { t: "Ready", c: "text-white/40" };

  return (
    <div className="bg-[#101116] border border-white/5 rounded-sm p-6" data-testid="automation-timeline">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm uppercase tracking-[0.18em] text-brand">Automated follow-up</h2>
        <span className="text-[10px] uppercase tracking-wider bg-brand/15 text-brand px-2 py-0.5 rounded-sm">Auto</span>
      </div>
      <ol className="space-y-3">
        <li className="flex items-center justify-between text-sm">
          <span className="flex items-center gap-2 text-white/80"><PaperPlaneTilt size={14} weight="fill" className="text-brand" /> Instant first touch (email)</span>
          <span className={`text-xs ${firstChip.c}`}>{firstChip.t}</span>
        </li>
        {items.map((it) => {
          const s = statusChip[it.status] || statusChip.pending;
          return (
            <li key={it.id} className="flex items-center justify-between text-sm" data-testid={`drip-step-${it.step}`}>
              <span className="flex items-center gap-2 text-white/70">
                <EnvelopeSimpleOpen size={14} weight="duotone" className="text-white/40" />
                Nudge #{it.step - 1} · {fmt(it.send_at)}
              </span>
              <span className={`text-xs ${s.c}`}>{s.t}</span>
            </li>
          );
        })}
      </ol>
      {items.length === 0 && emailStatus == null && (
        <p className="text-xs text-white/40 mt-3">Connect Resend to activate automated follow-ups for new leads.</p>
      )}
    </div>
  );
}

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/leads/${id}`);
      setLead(data);
      setNotes(data.notes || "");
    } catch {
      toast.error("Lead not found");
      navigate("/dashboard/leads");
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (lead?.ai_status === "pending") {
      const t = setInterval(load, 4000);
      return () => clearInterval(t);
    }
  }, [lead?.ai_status, load]);

  const updateStatus = async (status) => {
    const { data } = await api.patch(`/leads/${id}`, { status });
    setLead(data);
    toast.success(`Status updated to ${status}`);
  };

  const saveNotes = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/leads/${id}`, { notes });
      setLead(data);
      toast.success("Notes saved");
    } finally { setSaving(false); }
  };

  const rescore = async () => {
    await api.post(`/leads/${id}/rescore`);
    toast.message("Re-scoring with AI…");
    setLead((l) => ({ ...l, ai_status: "pending" }));
  };

  const setFollowUp = async (days) => {
    const when = new Date(Date.now() + days * 86400000).toISOString();
    const { data } = await api.patch(`/leads/${id}`, { next_follow_up_at: when });
    setLead(data);
    toast.success(`Follow-up set for ${new Date(when).toLocaleDateString()}`);
  };

  if (!lead) return <div className="text-white/40 text-sm">Loading…</div>;
  const pending = lead.ai_status === "pending";

  return (
    <div className="space-y-6" data-testid="lead-detail-page">
      <Link to="/dashboard/leads" className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-white transition-colors">
        <ArrowLeft size={16} /> Back to leads
      </Link>

      <div className="bg-[#101116] border border-white/5 rounded-sm p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="h-14 w-14 rounded-sm bg-brand/10 border border-brand/20 flex items-center justify-center text-brand text-xl font-serif">
            {lead.name?.[0]?.toUpperCase()}
          </div>
          <div>
            <h1 className="text-2xl text-white">{lead.name}</h1>
            <p className="text-sm text-white/40 capitalize">{lead.lead_type} lead · {lead.timeline || "no timeline"}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {lead.score_numeric != null && !pending && (
            <div className="text-right">
              <p className="text-3xl text-white font-serif leading-none">{lead.score_numeric}</p>
              <p className="text-[10px] uppercase tracking-wider text-white/40">AI Score</p>
            </div>
          )}
          <TierBadge tier={lead.score_tier} pending={pending} />
        </div>
      </div>

      {lead.property_id && (
        <Link to={`/listings/${lead.property_id}`} target="_blank" data-testid="inquired-property" className="flex items-center gap-2 bg-brand/10 border border-brand/20 rounded-sm px-4 py-3 text-sm text-brand hover:bg-brand/15 transition-colors">
          <House size={16} weight="duotone" /> Inquired about: <span className="text-white">{lead.property_title}</span>
        </Link>
      )}

      <div className="flex flex-wrap gap-3">
        <a href={`mailto:${lead.email}`} data-testid="action-email" className="flex items-center gap-2 px-4 py-2.5 rounded-sm border border-white/10 text-sm text-white/70 hover:text-brand hover:border-brand/40 transition-colors"><EnvelopeSimple size={16} /> Email</a>
        <a href={`tel:${lead.phone}`} data-testid="action-call" className="flex items-center gap-2 px-4 py-2.5 rounded-sm border border-white/10 text-sm text-white/70 hover:text-brand hover:border-brand/40 transition-colors"><Phone size={16} /> Call</a>
        <a href={`sms:${lead.phone}`} data-testid="action-sms" className="flex items-center gap-2 px-4 py-2.5 rounded-sm border border-white/10 text-sm text-white/70 hover:text-brand hover:border-brand/40 transition-colors"><ChatCircleText size={16} /> Text</a>
        <button onClick={rescore} data-testid="rescore-btn" className="flex items-center gap-2 px-4 py-2.5 rounded-sm border border-white/10 text-sm text-white/70 hover:text-brand hover:border-brand/40 transition-colors ml-auto"><ArrowsClockwise size={16} /> Re-score</button>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="followup-row">
        <span className="uppercase tracking-wider text-white/40">Follow up:</span>
        {[["Tomorrow", 1], ["3 days", 3], ["1 week", 7]].map(([label, days]) => (
          <button
            key={days}
            type="button"
            data-testid={`followup-${days}`}
            onClick={() => setFollowUp(days)}
            className="px-3 py-1.5 rounded-sm border border-white/10 text-white/60 hover:text-brand hover:border-brand/40"
          >
            {label}
          </button>
        ))}
        {lead.next_follow_up_at && (
          <span className="text-brand ml-1" data-testid="followup-current">
            Due {new Date(lead.next_follow_up_at).toLocaleDateString()}
          </span>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="space-y-6">
          <div className="bg-[#101116] border border-white/5 rounded-sm p-6 space-y-4">
            <h2 className="text-sm uppercase tracking-[0.18em] text-white/50 mb-2">Contact & criteria</h2>
            <Detail label="Email" value={lead.email} />
            <Detail label="Phone" value={lead.phone} />
            <Detail label="Budget" value={lead.budget} />
            <Detail label="Areas" value={lead.areas} />
            <Detail label="Property type" value={lead.property_type} />
            <Detail label="Bedrooms" value={lead.bedrooms} />
            <Detail label="Financing" value={lead.financing} />
            <Detail label="Address" value={lead.address} />
            <Detail label="Condition" value={lead.condition} />
            <Detail label="Reason" value={lead.reason} />
            <Detail label="Message" value={lead.message} />
          </div>

          <div className="bg-[#101116] border border-white/5 rounded-sm p-6">
            <p className="text-[11px] uppercase tracking-[0.15em] text-white/40 mb-3">Pipeline status</p>
            <Select value={lead.status} onValueChange={updateStatus}>
              <SelectTrigger data-testid="status-select" className="bg-[#0B0C10] border-white/10 text-white capitalize"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#101116] border-white/10 text-white">
                {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s} data-testid={`status-option-${s}`} className="capitalize">{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="bg-[#101116] border border-white/5 rounded-sm p-6">
            <p className="text-[11px] uppercase tracking-[0.15em] text-white/40 mb-3">Private notes</p>
            <textarea data-testid="notes-input" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Add a note about this lead…" className="w-full bg-[#0B0C10] border border-white/10 rounded-sm p-3 text-sm text-white placeholder-white/30 outline-none focus:border-brand/50 resize-none" />
            <button onClick={saveNotes} disabled={saving} data-testid="save-notes-btn" className="mt-3 flex items-center gap-2 px-4 py-2.5 rounded-sm bg-brand text-black text-xs uppercase tracking-wider font-medium hover:bg-brand-hover transition-colors disabled:opacity-60">
              <FloppyDisk size={14} /> {saving ? "Saving…" : "Save notes"}
            </button>
          </div>
        </div>

        <div className="lg:col-span-2 space-y-6">
          <div className="bg-[#101116] border border-white/5 rounded-sm p-6">
            <h2 className="text-sm uppercase tracking-[0.18em] text-brand mb-3">AI Lead Summary</h2>
            {pending ? (
              <div className="flex items-center gap-3 text-white/50 text-sm py-4"><span className="h-4 w-4 rounded-full border-2 border-brand border-t-transparent animate-spin" /> Analyzing this lead with AI…</div>
            ) : lead.ai_status === "failed" ? (
              <p className="text-white/50 text-sm">AI analysis unavailable. Try re-scoring.</p>
            ) : (
              <p className="text-white/80 leading-relaxed">{lead.ai_summary || "—"}</p>
            )}
          </div>

          {!pending && lead.ai_status === "done" && (
            <>
              <AutomationTimeline leadId={id} emailStatus={lead.email_status} />
              <EditableDraft leadId={id} kind="email" title="Follow-up email" icon={EnvelopeSimpleOpen} initial={lead.ai_followup_email} sentStatus={lead.email_status} onSent={(s) => setLead((l) => ({ ...l, email_status: s }))} />
              <EditableDraft leadId={id} kind="sms" title="Follow-up SMS" icon={ChatCircleText} initial={lead.ai_followup_sms} sentStatus={lead.sms_status} onSent={(s) => setLead((l) => ({ ...l, sms_status: s }))} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
